import {FC, memo} from 'react';

import {Box, Typography, useMediaQuery} from '@mui/material';
import {t} from 'i18next';

import ChevronRight from 'assets/icons/chevronRight';
import Points from 'assets/icons/points';
import PointsGold from 'assets/icons/pointsGold';
import theme from 'config/theme';
import {useAppSelector} from 'hooks';
import ReactNative from 'services/react-native';
import SendBrowserEvent from 'services/send-browser-event';
import {Promotion} from 'types';
import {formatDate, formatPriceWithoutDecimal} from 'utils';

import {
  StyledProPointsSuggestionContainer,
  StyledSectionHeader,
  StyledSectionContentContainer,
  StyledSeeMoreBtn,
  StyledProgressBox,
  StyledProgressBar
} from './styles';

const formatPromotionDate = formatDate('DD MMMM YYYY');

const ProPointSuggestions: FC = () => {
  const promotions = useAppSelector((state) => state.basket?.promotions);
  const isReactNativeView = ReactNative.isReactNativeWebView();
  const isMobileView = useMediaQuery(theme.breakpoints.only('mobile'));

  let firstPromo: Promotion | null = null;

  if (promotions?.promotionSuggestions?.length) {
    firstPromo = promotions.promotionSuggestions[0]?.promotions[0];
  }

  const navigateToProPointPromotionPage = () => {
    SendBrowserEvent.goToBasketProPointPromotions();
  };

  const openPromotionDetailModal = () => {
    SendBrowserEvent.openProPointPromotionDetailModal(firstPromo);
  };

  const progress = (firstPromo?.progress ?? 0) / (firstPromo?.conditionDetail?.targetValue ?? 1);

  if (!firstPromo) {
    return null;
  }
  return (
    <StyledProPointsSuggestionContainer isReactNativeView={isReactNativeView || isMobileView}>
      <StyledSectionHeader>
        <Box display="flex" alignItems="center" gap="4px">
          <Points size={20} />
          <Typography fontWeight={600} lineHeight="24px" color="#212124">
            {t('proPointPromotion.title')}
          </Typography>
        </Box>
        <StyledSeeMoreBtn onClick={navigateToProPointPromotionPage}>
          <Typography fontWeight={600} fontSize="14px" color="#212124">
            {t('proPointPromotion.seeMore')}
          </Typography>
          <ChevronRight />
        </StyledSeeMoreBtn>
      </StyledSectionHeader>
      <StyledSectionContentContainer onClick={openPromotionDetailModal}>
        <Box>
          <Typography fontWeight={600} fontSize="14px" color="#212124">
            {t('proPointPromotion.spendSuggestion', {amount: formatPriceWithoutDecimal(firstPromo?.suggestionAmount)})}
          </Typography>
          <StyledProgressBox>
            <span className="progressBar-coin" style={{left: `${Math.min(progress * 100, 98)}%`}}>
              <PointsGold size={24} />
            </span>
            <StyledProgressBar className="promotion-plp-page" variant="determinate" value={progress * 100} />
          </StyledProgressBox>
        </Box>
        <Box>
          <Typography fontSize="10px" lineHeight="16px" color="#6B6B6B">
            {t('proPointPromotion.pointsToEarn')}
          </Typography>
          <Typography textAlign="right" fontWeight={600} fontSize="14px" color="#212124">
            <Points size={12} /> {firstPromo?.conditionDetail?.rewardValue}
          </Typography>
        </Box>
        <Box mt="8px" flexBasis="100%">
          <Typography
            fontSize="10px"
            lineHeight="16px"
            color="#212124"
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            {firstPromo?.campaignName} <ChevronRight width={12} height={12} />
          </Typography>
          <Typography fontSize="10px" lineHeight="16px" color="#6B6B6B">
            {t('proPointPromotion.unlockBefore')} {formatPromotionDate(firstPromo?.endDate)}
          </Typography>
        </Box>
      </StyledSectionContentContainer>
    </StyledProPointsSuggestionContainer>
  );
};

export default memo(ProPointSuggestions);
